import type { ContextOnSubmit } from '@feathery/react/dist/types/Form';
import type {
  BusinessInterests,
  CrbMarketingUserOnboardingFormDataV1_2,
  DueDiligenceStatus,
  InputMarketingOrgRegistration,
  User,
  UserInterests
} from '@gcv/shared';
import { FeatheryInterestUserInterestMap, PlantTouchingIndustryRoles, USStates } from '@gcv/shared';
import { inject, injectable } from 'inversify';
import { action, makeAutoObservable, observe } from 'mobx';

import { USER_KEY } from 'hooks/keys';
import { CrbBankStore } from 'stores/CrbBankStore';
import { CrbDispensaryStore } from 'stores/CrbDispensaryStore';
import { UserStore } from 'stores/UserStore';
import { updatePendoForCrb } from 'third-party-integrations/pendo';
import { stripCountryCode } from 'util/phone.util';
import type { PM } from './welcome.repo';
import { CrbWelcomeRepo } from './welcome.repo';

interface VM {
  isLoading: boolean;
  formData: CrbMarketingUserOnboardingFormDataV1_2;
  user: null | User;
}

@injectable()
export class CrbWelcomePresenter {
  @inject(CrbWelcomeRepo)
  repo: CrbWelcomeRepo;

  @inject(CrbDispensaryStore)
  dispensaryStore: CrbDispensaryStore;

  @inject(UserStore)
  userStore: UserStore;

  @inject(CrbBankStore)
  crbBankStore: CrbBankStore;

  vm: VM = {
    isLoading: true,
    formData: {},
    user: null
  };

  constructor() {
    makeAutoObservable(this);
  }

  public load = async () => {
    observe(this.repo, 'programmersModel', (obj) => {
      const programmersModel = obj.newValue as PM;

      this.vm.formData = programmersModel.formData;
    });

    this.vm.user = this.userStore.user;
    this.repo.load().then(() => {
      this.setIsLoading(false);
    });
  };

  private isPlantTouching = (role: string) => role in PlantTouchingIndustryRoles;

  private mapUserInterests = (formInterests?: BusinessInterests[]): UserInterests[] => {
    return formInterests?.map((fi) => FeatheryInterestUserInterestMap[fi]) ?? [];
  };

  public onSubmit = async (context: ContextOnSubmit): Promise<boolean> => {
    const formData = context.getFieldValues() as CrbMarketingUserOnboardingFormDataV1_2;
    const userInterests = this.mapUserInterests(formData['interests_1']);

    const data: InputMarketingOrgRegistration = {
      termsOfService: Boolean(formData['terms-of-service_1']),
      companyName: formData['business-name_1']?.trim() ?? '',
      phone: stripCountryCode(formData['phone_1'] ?? ''), // feathery includes the country code, therefore exclude it here
      plantTouching: this.isPlantTouching(formData['business-role_1'] ?? ''),
      state: USStates[formData['state_1']?.replaceAll(' ', '_') as keyof typeof USStates],
      userInterests,
      monthlyCustomers: 0
    };

    if (context.isLastStep()) {
      await this.repo.registerMarketingUserOrg(data);
      await this.userStore.queryClient.invalidateQueries({ queryKey: [USER_KEY, this.userStore.user?.id] });
      await this.userStore.load(this.userStore.user.id);

      const dispensaryId = this.userStore.user.companies[0].id;
      await this.dispensaryStore.loadDispensaries(this.userStore.user.id);
      await this.dispensaryStore.setCurrentDispensary(dispensaryId);

      const provider = this.dispensaryStore.getDefaultProvider();
      const activeServiceProviders = this.crbBankStore.getActiveCrbServiceProviders();
      const showCompetitiveMarketPlaceOffers = activeServiceProviders.some(
        (sp) => sp.showCompetitors || sp.activeServiceProvider === false
      );
      updatePendoForCrb(
        this.dispensaryStore.currentDispensary,
        this.userStore.user,
        this.dispensaryStore.currentDispensary.name,
        provider.id,
        provider.templates.value.onboarding.status as DueDiligenceStatus,
        activeServiceProviders.length,
        activeServiceProviders,
        showCompetitiveMarketPlaceOffers,
        {
          role: formData['business-role_1'],
          interests: formData['interests_1'],
          userInterests,
          banking_status: formData['banking-status_1'],
          is_plant_touching: this.dispensaryStore.currentDispensaryIsPlantTouching
        }
      );
      return true;
    }
    return false;
  };

  onChange = (formData: Record<string, unknown>) => this.repo.updateUserFeatheryFormResponse(formData);

  setIsLoading = action((isLoading: boolean) => {
    this.vm.isLoading = isLoading;
  });

  setFormData = (formData: Record<string, unknown>) => {
    this.vm.formData = formData;
  };
}
